import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { en } from './en'
import { el } from './el'

export const resources = { en: { translation: en }, el: { translation: el } }

/**
 * Its own storage key, not the funnel's `bonded_locale`. The two apps are
 * independent and must not be able to affect each other's state — including
 * through a shared localStorage key on a shared parent domain.
 */
export const LOCALE_KEY = 'bonded_research_locale'

export type Locale = 'en' | 'el'

export const LOCALES: readonly { value: Locale; label: string }[] = [
	// Native names, not translated: a language picker that renames the languages
	// into the language you cannot read is useless to the person who needs it.
	{ value: 'en', label: 'English' },
	{ value: 'el', label: 'Ελληνικά' },
]

/**
 * English by default.
 *
 * Deliberately no `navigator.language` sniffing. The survey is fielded to an
 * international audience through links whose copy is written in English, and a
 * Greek-locale browser landing on an English post should not be silently
 * switched — the picker in the header is one tap away. A stored choice always
 * wins, so the switch is remembered once made.
 */
export function initialLocale(): Locale {
	try {
		const stored = localStorage.getItem(LOCALE_KEY)
		if (stored === 'el' || stored === 'en') return stored
	} catch {
		// Storage can throw in a locked-down or private context; fall through.
	}
	return 'en'
}

void i18n.use(initReactI18next).init({
	resources,
	lng: initialLocale(),
	fallbackLng: 'en',
	interpolation: { escapeValue: false },
})

/**
 * Keep `<html lang>` in step with the active locale.
 *
 * `index.html`'s static `lang="en"` is only ever correct for an English visit
 * — it never updated when someone switched to Greek from the header picker, so
 * a Greek-reading page kept claiming to be English. That is a real correctness
 * bug independent of `noindex`: screen readers pick pronunciation off `lang`,
 * and the browser's own translate prompt keys off it too.
 *
 * Set once for the initial locale, then kept live via i18next's own event.
 */
document.documentElement.lang = i18n.language
i18n.on('languageChanged', (lng) => {
	document.documentElement.lang = lng
})

export default i18n
